"use client";

import { useCallback, useEffect, useState } from "react";
import Script from "next/script";
import Link from "next/link";
import { cn } from "@/lib/cn";

const STORAGE_KEY = "fyt-cookie-consent";
type Choice = "granted" | "denied";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Bannière de consentement cookies + GA4 en Consent Mode v2.
 * - GA4 ne se charge que si `gaId` est défini (NEXT_PUBLIC_GA_ID).
 * - Consentement par défaut : refusé. Aucun cookie de mesure avant acceptation.
 * - Le choix est persisté en localStorage ; ré-ouvrable via le lien « Cookies » du footer
 *   (événement `fyt:open-cookie-settings`).
 */
export function CookieConsent({ gaId }: { gaId?: string }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // localStorage peut throw (Safari navigation privée / stockage bloqué) :
    // surtout pas casser toute la page pour une bannière cookies.
    const stored = safeGetConsent();
    if (stored === "granted") updateConsent("granted");
    if (!stored) setVisible(true);
  }, []);

  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener("fyt:open-cookie-settings", reopen);
    return () => window.removeEventListener("fyt:open-cookie-settings", reopen);
  }, []);

  const choose = useCallback((choice: Choice) => {
    safeSetConsent(choice);
    updateConsent(choice);
    setVisible(false);
  }, []);

  if (!gaId) return null;

  return (
    <>
      {/* Consent Mode v2 : par défaut tout est refusé, gtag chargé en mode dégradé. */}
      <Script id="ga-consent-default" strategy="afterInteractive">
        {`window.dataLayer = window.dataLayer || [];
window.gtag = function(){ window.dataLayer.push(arguments); };
gtag('consent','default',{ad_storage:'denied',analytics_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',wait_for_update:500});
gtag('js', new Date());
gtag('config','${gaId}');`}
      </Script>
      <Script
        id="ga-lib"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
      />

      {visible && (
        <div
          role="dialog"
          aria-label="Gestion des cookies"
          className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-card px-5 py-5 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] md:px-12"
        >
          <div className="mx-auto flex w-full max-w-[1080px] flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <p className="text-[13px] leading-[1.6] text-body">
              Nous utilisons des cookies de mesure d'audience (Google Analytics) pour
              améliorer votre expérience. Vous pouvez accepter ou refuser.{" "}
              <Link
                href="/confidentialite"
                className="text-ink underline underline-offset-2 hover:text-gold-deep"
              >
                En savoir plus
              </Link>
            </p>
            <div className="flex shrink-0 gap-3">
              <button
                type="button"
                onClick={() => choose("denied")}
                className="inline-flex items-center justify-center rounded-pill border border-line-strong px-5 py-2.5 text-[12px] font-semibold uppercase tracking-wider text-ink transition-colors hover:bg-bg-warm"
              >
                Refuser
              </button>
              <button
                type="button"
                onClick={() => choose("granted")}
                className={cn(
                  "inline-flex items-center justify-center rounded-pill px-5 py-2.5 text-[12px] font-semibold uppercase tracking-wider text-ink transition-colors",
                  "bg-gold hover:bg-gold-deep",
                )}
              >
                Accepter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function safeGetConsent(): Choice | null {
  try {
    return (localStorage.getItem(STORAGE_KEY) as Choice | null) ?? null;
  } catch {
    return null;
  }
}

function safeSetConsent(choice: Choice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // stockage indisponible : on respecte quand même le choix sur la session
    // courante (updateConsent), simplement il ne sera pas mémorisé.
  }
}

function updateConsent(choice: Choice) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    analytics_storage: choice,
  });
}
